import React, { FC, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Popper, Fade, Avatar, Typography, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CakeIcon from '@mui/icons-material/Cake';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import GroupsIcon from '@mui/icons-material/Groups';
import userService from '../services/user';
import herdService from '../services/herds';
import UserAvatar from './UserAvatar';
import UserName from './UserName';

type PreviewType = 'user' | 'herd';

interface HoverPreviewCardProps {
  type: PreviewType;
  name: string;
  children: ReactElement;
}

// Hover cards are a pointer affordance only; on touch, links keep navigating.
const HOVER_CAPABLE =
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: hover)').matches;

// Module-level cache + in-flight dedupe so re-hovering the same name is instant
// and never refetches within a session.
const previewCache = new Map<string, any>();
const inflight = new Map<string, Promise<any>>();

const cacheKey = (type: PreviewType, name: string) => `${type}:${name.toLowerCase()}`;

const fetchPreview = (type: PreviewType, name: string): Promise<any> => {
  const key = cacheKey(type, name);
  if (previewCache.has(key)) return Promise.resolve(previewCache.get(key));
  if (inflight.has(key)) return inflight.get(key) as Promise<any>;

  const request = (type === 'user'
    ? userService.getUserPreview(name)
    : herdService.getHerdPreview(name)
  )
    .then((data: any) => {
      previewCache.set(key, data);
      inflight.delete(key);
      return data;
    })
    .catch((err: any) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, request);
  return request;
};

// "Jul 07 2026" — mirrors the join-date format used on the profile/herd pages.
const formatJoined = (createdAt?: string) =>
  createdAt ? String(new Date(createdAt)).split(' ').slice(1, 4).join(' ') : '';

const OPEN_DELAY_MS = 320;
const CLOSE_DELAY_MS = 180;

const HoverPreviewCard: FC<HoverPreviewCardProps> = ({ type, name, children }) => {
  const theme = useTheme();
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const [open, setOpen] = useState(false);
  const [data, setData] = useState<any>(() => previewCache.get(cacheKey(type, name)) ?? null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>(
    previewCache.has(cacheKey(type, name)) ? 'loaded' : 'idle'
  );

  useEffect(
    () => () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
    },
    []
  );

  const load = useCallback(() => {
    setStatus(prev => (prev === 'loaded' ? prev : 'loading'));
    fetchPreview(type, name)
      .then(d => {
        setData(d);
        setStatus('loaded');
      })
      .catch(() => setStatus('error'));
  }, [type, name]);

  const handleOpen = useCallback(() => {
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => {
      setOpen(true);
      // load() is cache-aware + dedupes, so this hydrates local state from the
      // cache instantly when warm, or fetches once when cold.
      load();
    }, OPEN_DELAY_MS);
  }, [load]);

  const handleClose = useCallback(() => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, []);

  // On touch / non-hover devices, render the trigger untouched.
  if (!HOVER_CAPABLE) return children;

  const cardWidth = 288;

  const statTile = (label: string, value: React.ReactNode) => (
    <Box
      key={label}
      sx={{
        flex: 1,
        borderRadius: 'var(--sh-md)',
        p: '8px 6px',
        textAlign: 'center',
        backgroundColor: theme.tokens.colors.backgroundCardHover,
        border: `1px solid ${theme.tokens.colors.glassLine}`,
      }}
    >
      <Typography
        sx={{
          fontFamily: theme.tokens.typography.sansFontFamily,
          fontWeight: theme.tokens.typography.fontWeightBold,
          fontSize: '14px',
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: theme.tokens.typography.monoFontFamily,
          fontSize: '9px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: theme.tokens.colors.text3,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  const renderUser = () => {
    const points = data.reactionPoints ?? 0;
    return (
      <>
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', mb: 1.25 }}>
          <UserAvatar
            user={{ username: data.username as string, avatar: data.avatar, cos: (data as any).cos }}
            size={48}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontFamily: theme.tokens.typography.displayFontFamily, fontSize: '16px', lineHeight: 1.3 }}
            >
              <UserName username={data.username as string} prefix="@" />
            </Typography>
            <Typography
              sx={{ fontSize: '12px', color: theme.tokens.colors.text3, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <CakeIcon sx={{ fontSize: 13 }} /> Joined {formatJoined(data.createdAt)}
            </Typography>
          </Box>
        </Box>
        {data.streak > 0 && (
          <Typography
            sx={{ fontSize: '12px', color: theme.tokens.colors.text2, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
          >
            <LocalFireDepartmentIcon sx={{ fontSize: 15, color: theme.tokens.colors.primary }} />
            {data.streak}-day streak
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {statTile('Points', points)}
          {statTile('Posts', data.postCount ?? 0)}
          {statTile('Comments', data.totalComments ?? 0)}
          {statTile('Awards', data.achievementsUnlocked ?? 0)}
        </Box>
      </>
    );
  };

  const renderHerd = () => (
    <>
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', mb: 1.25 }}>
        {data.herdImage ? (
          <Avatar src={data.herdImage} alt={data.herdName} sx={{ width: 48, height: 48 }} />
        ) : (
          <Avatar
            sx={{
              width: 48,
              height: 48,
              backgroundColor: theme.tokens.colors.backgroundCardHover,
              color: theme.tokens.colors.text2,
              fontFamily: theme.tokens.typography.displayFontFamily,
              fontSize: '20px',
            }}
          >
            {data.herdName?.[0]?.toUpperCase()}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontFamily: theme.tokens.typography.displayFontFamily, fontSize: '16px', lineHeight: 1.3 }}
          >
            {data.herdName}
          </Typography>
          <Typography
            sx={{ fontSize: '12px', color: theme.tokens.colors.text3, display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <CakeIcon sx={{ fontSize: 13 }} /> Created {formatJoined(data.createdAt)}
          </Typography>
        </Box>
      </Box>
      {data.description && (
        <Typography
          sx={{
            fontSize: '12.5px',
            color: theme.tokens.colors.text2,
            mb: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {data.description}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        {statTile(
          data.memberCount === 1 ? 'Member' : 'Members',
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <GroupsIcon sx={{ fontSize: 15 }} />
            {data.memberCount ?? 0}
          </Box>
        )}
        {statTile('Posts', data.postCount ?? 0)}
      </Box>
    </>
  );

  return (
    <>
      <Box
        component="span"
        ref={anchorRef}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        sx={{ display: 'inline' }}
      >
        {children}
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="top-start"
        transition
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        sx={{ zIndex: theme.zIndex.tooltip }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={140}>
            <Box
              onMouseEnter={() => clearTimeout(closeTimer.current)}
              onMouseLeave={handleClose}
              sx={{
                width: cardWidth,
                p: 1.5,
                borderRadius: 'var(--sh-lg)',
                backgroundColor: theme.tokens.colors.backgroundCard,
                border: `1px solid ${theme.tokens.colors.glassLine}`,
                boxShadow: theme.tokens.shadows.card,
              }}
            >
              {status === 'error' ? (
                <Typography sx={{ fontSize: '13px', color: theme.tokens.colors.text3, py: 1, textAlign: 'center' }}>
                  Couldn’t load preview.
                </Typography>
              ) : !data ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : type === 'user' ? (
                renderUser()
              ) : (
                renderHerd()
              )}
            </Box>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default HoverPreviewCard;
